import React, {useEffect, useState} from "react";
import Typography from '@mui/material/Typography';

import {useTranslation} from "react-i18next";
import {
  useSummaryByOrderIdQuery,
  useSummary,
  useUpdateSummaryByOrderIdMutation,
  useGetInvoices,
  useDeclarationsByOrderIdQuery,
  useSetCompletedByOrderIdMutation,
  useAddServicesToRouteByOrderIdMutation,
} from "../../../../../../../api/calc";

import './summary.scss'
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {useDemoPackagingSummary} from 'components/cabinet/demo/steps/packaging/use-packaging';
import CombinedCargo from './components/combined_cargo';
import DeliveryType from './components/delivery_type';
import { Completed } from '@vedmarket/ui-kit';
import SavedMethod from './components/saved-method';
import EditContainers from "./components/edit_containers";
import isEmpty from "lodash/isEmpty";
import COLORS from "../../../../../../../styles/colors.scss";
import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../utils/helpers";
import {useGetContainersQuery} from "../../../../../../../api/containers";
import {useCalculationReadOnly} from '../../../../calculation-read-only';

export const enum METHODS {
  COMBINED_CARGO = 1,
  CONTAINERS = 2
}

export const getContainerFieldCode = (code: string) => code.startsWith('type_') ? code : `type_${code}`;

export const getContainerDataValue = (containersData: ContainersManual | Record<string, never> | undefined, code: string) => containersData?.[code] ?? containersData?.[code.replace(/^type_/, '')];

export const getContainerFieldItems = (containers: ContainerDictionary[], containersData?: ContainersManual | Record<string, never>) => {
  const dictionaryCodes = containers.map((item) => item.code ? getContainerFieldCode(item.code) : '').filter(Boolean);
  const summaryCodes = Object.keys(containersData || {}).map(getContainerFieldCode);
  const codes = [ ...dictionaryCodes, ...summaryCodes.filter((code) => !dictionaryCodes.includes(code)) ];

  return codes.map((code) => {
    const container = containers.find((item) => getContainerFieldCode(item.code) === code);

    return {
      code,
      label: container?.label || code,
    }
  })
}

const Summary = ({productsCount, handleComplete}: PackagingSummaryProps) => {

  const [ deliveryMethod, setDeliveryMethod ] = useState<number | undefined>(undefined);

  const [ editMode, setEditMode ] = useState(false);
  const readOnly = useCalculationReadOnly();

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const invoices = useGetInvoices()
  const { data: containers = [] } = useGetContainersQuery()

  const { data: summary, refetch, isSuccess } = useSummaryByOrderIdQuery(orderId, {skip: !orderId})

  const summaryContainers = useSummary()

  const containersUser = summaryContainers?.containers_user;
  const currentDeliveryType = summaryContainers?.delivery_type;

  const { refetch: refetchDeclaration } = useDeclarationsByOrderIdQuery(orderId, { skip: !currentDeliveryType || !orderId })

  const [ saveSummary ] = useUpdateSummaryByOrderIdMutation()
  const [ setCompleted ] = useSetCompletedByOrderIdMutation()
  const [ addServices ] = useAddServicesToRouteByOrderIdMutation()


  useEffect( () => {
    refetch();
  }, [ invoices.length, productsCount ])

  useEffect(() => {
    !isSuccess && refetch();
  }, [])

  const demoPlayback = useDemoPackagingSummary({
    currentDeliveryType,
    deliveryType: summary?.delivery_type,
    hasSummary: Boolean(summary),
    setDeliveryMethod,
  })

  if (!summary?.products_count) {
    return null;
  }

  const handleSendCompleted = async () => {
    await setCompleted({
      orderId,
      packaging: true,
      delivery: false,
      customs: false,
      additional_services: false,
    })

    await addServices({
      orderId,
      route: null,
      services: []
    });
  }

  const handleSave = async () => {
    let savedData: UpdateSummaryParamsInput = {
      orderId,
      delivery_type: deliveryMethod,
    }

    if (deliveryMethod === METHODS.CONTAINERS) {
      savedData = {
        ...savedData,
        containers_manual: containersUser
      }
    }

    const result = await saveSummary(savedData)

    if (isCalculationViewModeError(result)) {
      return;
    }

    if (deliveryMethod !== summary.delivery_type) {
      await handleSendCompleted()
    }

    refetch();
    refetchDeclaration();
    handleComplete();
    setEditMode(false);
    demoPlayback.confirmDelivery()
  }

  const handleEditSave = async (values?: ContainersManual) => {
    setDeliveryMethod(METHODS.CONTAINERS)

    const savedData: UpdateSummaryParamsInput = {
      orderId,
      delivery_type: METHODS.CONTAINERS,
      containers_manual: values
    }

    const result = await saveSummary(savedData)

    if (isCalculationViewModeError(result) || 'error' in result) {
      return false;
    }

    if (summary.delivery_type !== METHODS.CONTAINERS) {
      await handleSendCompleted()
    }

    setEditMode(false)

    refetch();
    refetchDeclaration();
    demoPlayback.confirmDelivery()
    return true;
  }

  const info = [
    {
      label: t('calc__text__packaging__summaryInvoiceCount'),
      value: summary.invoices_count,
    },
    {
      label: t('common__text__totalVolume'),
      value: <>{summary.volume_total} {t('units__text__meter')}<Typography component='sup' variant='inherit' sx={{fontSize: '0.645em', lineHeight: 0}}>3</Typography></>,
    },
    {
      label: t('calc__text__packaging__totalProducts'),
      value: `${summary.products_count} ${t('units__text__items')}`,
    },
    {
      label: t('calc__text__packaging__summaryGoodsPrice'),
      value: `${numberWithSpaces(summary.price_total)} USD`,
    },
    {
      label: t('common__text__netGrossWeight'),
      value: <>{summary.net_weight_total} {t('units__text__kg')} <Typography component='span' variant='inherit' sx={{color: COLORS.LIGHT_TEXT_DISABLED}}>/</Typography> {summary.gross_weight_total} {t('units__text__kg')}</>,
    },
  ];

  return <div className={`summary-packaging${demoPlayback.savedDeliveryType && !editMode ? ' summary-packaging_saved' : ''}`}>
    <Typography variant='subtitle1' sx={{fontWeight: 500, lineHeight: '24px', letterSpacing: 0, color: COLORS.BLACK_DARK}}>
      {t('calc__text__packaging__preliminarySummaryAboutPackage')}
    </Typography>
    <Completed info={info} noPadding isFullRow />
    {!readOnly && (editMode || !demoPlayback.savedDeliveryType) ? <div className='summary-packaging__delivery'>
      <DeliveryType
        deliveryMethod={deliveryMethod}
        demoDeliveryMethod={demoPlayback.demoDeliveryMethod}
        setDeliveryMethod={setDeliveryMethod}
      />
      {deliveryMethod === METHODS.COMBINED_CARGO && <CombinedCargo handleSave={handleSave} summary={summary} />}
      {deliveryMethod === METHODS.CONTAINERS && <EditContainers onSave={handleEditSave} />}
    </div> : <SavedMethod
      summary={summary}
      method={demoPlayback.savedDeliveryType}
      containers={containers}
      onEdit={() => setEditMode(true)}
    />}
  </div>;
}

export default Summary
